import { z } from 'zod'

import { CurrencyNumberObjectZ, TripIdZ, UserIdZ } from '@/types/common'
import { LocationZ } from '@/types/location'
import { SearchIdZ } from '@/types/search'
import { BookingSegmentIdZ } from '@/types/trip'

import { RailDirectionZ } from './common'

const RailsType = z.literal('rails')

export const PageDirectionZ = z.enum(['next', 'previous'])

export type PageDirection = z.infer<typeof PageDirectionZ>

export const RailSearchIdZ = z.string().brand<'RailSearchId'>()

export type RailSearchId = z.infer<typeof RailSearchIdZ>

// Requests

export const RailSearchRequestRouteZ = z.object({
	departure_date: z.string().datetime(),
	from_location: z.string(),
	from_location_type: z.enum(['rail']),
	to_location: z.string(),
	to_location_type: z.enum(['rail']),
	connecting_locations: z
		.object({
			location_type: z.string(),
			location_id: z.string(),
		})
		.array(),
})

export type RailSearchRequestRoute = z.infer<typeof RailSearchRequestRouteZ>

export const RailTravelersCardsZ = z.record(UserIdZ, z.string().array())

export type RailTravelersCards = z.infer<typeof RailTravelersCardsZ>

export const RailTravelersDoBZ = z.record(UserIdZ, z.iso.date())

export type RailTravelersDoB = z.infer<typeof RailTravelersDoBZ>

export const RailCardNumbersZ = z.record(z.string(), z.record(z.string(), z.string()))

export type RailCardNumbers = z.infer<typeof RailCardNumbersZ>

export const RailTravelClassZ = z.enum(['first', 'second'])

export type RailTravelClass = z.infer<typeof RailTravelClassZ>

export const RailSearchRequestDetailsZ = z.object({
	rails_type: RailDirectionZ,
	routes: RailSearchRequestRouteZ.array().min(1),
	card_id: z.string().optional(),
	corporate_id: z.string().optional(),
	users_to_rail_cards: RailTravelersCardsZ.optional(),
	users_dob: RailTravelersDoBZ.optional(),
	rail_card_numbers: RailCardNumbersZ.optional(),
	travel_class: RailTravelClassZ,
})

export type RailSearchRequestDetails = z.infer<typeof RailSearchRequestDetailsZ>

export const RailSearchRequestZ = z.object({
	type: RailsType,
	details: RailSearchRequestDetailsZ,
	travelers_ids: z.array(UserIdZ),
	trip_id: TripIdZ,
})

export type RailSearchRequest = z.infer<typeof RailSearchRequestZ>

export const RailSearchSetInwardRequestZ = z.object({
	search_id: RailSearchIdZ,
	outward_journey_id: z.string(),
	outward_alternative_ids: z.string().array().optional(),
})

export type RailSearchSetInwardRequest = z.infer<typeof RailSearchSetInwardRequestZ>

export const RailSearchExtendPageRequestZ = z.object({
	search_id: RailSearchIdZ,
	type: z.enum(['outward', 'inward']),
	page_direction: z.enum(['next', 'previous']),
	inward_search_id: z.string().optional(),
})

export type RailSearchExtendPageRequest = z.infer<typeof RailSearchExtendPageRequestZ>

const RefundAmountsZ = z
	.object({
		return_mode: z
			.object({
				name: z.string(),
				return_address: z.null(),
				description: z.string(),
			})
			.nullable(),
		type: z.enum(['fare-refund', 'inventory-fee', 'admin-fee']),
		amount: CurrencyNumberObjectZ,
	})
	.array()

const RailCancellationOfferZ = z
	.strictObject({
		bs_id: BookingSegmentIdZ,
		refund_amounts: RefundAmountsZ,
		converted_refund_amounts: RefundAmountsZ,
		total_refund_amount: CurrencyNumberObjectZ.nullable(),
		converted_total_refund_amount: CurrencyNumberObjectZ.nullable(),
		expires_at: z.string().datetime({ offset: true }).nullable(),
		description: z.string().nullish(),
	})
	.array()

export const RailCancellationOfferResultZ = RailCancellationOfferZ.nullable()

export type RailCancellationOffer = z.infer<typeof RailCancellationOfferZ>

// Responses

export const RailSearchRouteZ = z.object({
	departure_date: z.string().datetime(),
	from_location: LocationZ.nullable(),
	to_location: LocationZ.nullable(),
	connecting_locations: z
		.object({
			location_type: z.string(),
			location: LocationZ,
		})
		.array(),
})

export type RailSearchRoute = z.infer<typeof RailSearchRouteZ>

export const RailSearchDetailsZ = z.object({
	rails_type: RailDirectionZ,
	routes: RailSearchRouteZ.array().min(1),
	card_id: z.string().optional(),
	corporate_id: z.string().optional(),
	search_id: SearchIdZ.optional(),
	inward_search_id: z.string().optional(),
	page_direction: PageDirectionZ.optional(),
	users_to_rail_cards: RailTravelersCardsZ.optional(),
	users_dob: RailTravelersDoBZ.optional(),
	rail_card_numbers: RailCardNumbersZ.optional(),
	travel_class: RailTravelClassZ,
})

export type RailSearchDetails = z.infer<typeof RailSearchDetailsZ>

export const RailSearchResponseZ = z.object({
	type: RailsType,
	details: RailSearchDetailsZ,
	travelers: UserIdZ,
	trip_id: SearchIdZ,
})

export type RailSearchResponse = z.infer<typeof RailSearchResponseZ>

export function mapRailSearchRequestRoute(route: RailSearchRoute): RailSearchRequestRoute {
	return {
		departure_date: route.departure_date,
		from_location: route.from_location!.external_id!,
		from_location_type: 'rail',
		to_location: route.to_location!.external_id!,
		to_location_type: 'rail',
		connecting_locations: route.connecting_locations.map((locationDetails) => ({
			location_type: locationDetails.location_type,
			location_id: locationDetails.location.external_id!,
		})),
	}
}
